import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

interface CalendlyStyleCalendarProps {
  onDateSelect: (date: Date) => void
  selectedDate: Date | null
  availableDates: Date[]
  isLoading: boolean
  currentMonth: Date
  onMonthChange: (direction: 'prev' | 'next') => void
  colorScheme?: {
    primary: string
    secondary: string
  }
}

const ChevronLeftIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
)

const ChevronRightIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
)

const CalendlyStyleCalendar: React.FC<CalendlyStyleCalendarProps> = ({
  onDateSelect,
  selectedDate,
  availableDates,
  isLoading,
  currentMonth,
  onMonthChange,
  colorScheme
}) => {
  const { t, i18n } = useTranslation()
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  // Get the first day of the month and calculate calendar grid
  const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1)
  const lastDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0)
  const firstDayOfWeek = firstDayOfMonth.getDay() // 0 = Sunday, 1 = Monday, etc.
  
  // Create array of dates for the calendar grid
  const calendarDays: (Date | null)[] = []
  
  // Add empty cells for days before the first day of the month
  for (let i = 0; i < firstDayOfWeek; i++) {
    calendarDays.push(null)
  }
  
  // Add all days of the month
  for (let day = 1; day <= lastDayOfMonth.getDate(); day++) {
    calendarDays.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day))
  }

  const monthNames = [
    t('calendar.months.january', 'January'),
    t('calendar.months.february', 'February'),
    t('calendar.months.march', 'March'),
    t('calendar.months.april', 'April'),
    t('calendar.months.may', 'May'),
    t('calendar.months.june', 'June'),
    t('calendar.months.july', 'July'),
    t('calendar.months.august', 'August'),
    t('calendar.months.september', 'September'),
    t('calendar.months.october', 'October'),
    t('calendar.months.november', 'November'),
    t('calendar.months.december', 'December')
  ]

  const dayNames = [
    t('calendar.days.sun', 'Sun'),
    t('calendar.days.mon', 'Mon'),
    t('calendar.days.tue', 'Tue'),
    t('calendar.days.wed', 'Wed'),
    t('calendar.days.thu', 'Thu'),
    t('calendar.days.fri', 'Fri'),
    t('calendar.days.sat', 'Sat')
  ]

  const isDateAvailable = (date: Date) => {
    return availableDates.some(availableDate => 
      availableDate.toDateString() === date.toDateString()
    )
  }

  const isDateSelected = (date: Date) => {
    return selectedDate && selectedDate.toDateString() === date.toDateString()
  }

  const isDateInPast = (date: Date) => {
    return date < today
  }

  const formatMonthYear = (date: Date) => {
    return `${monthNames[date.getMonth()]} ${date.getFullYear()}`
  }

  const canNavigatePrev = () => {
    const prevMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
    const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1)
    return prevMonth >= currentMonthStart
  }

  const primaryColor = colorScheme?.primary || '#3B82F6'
  
  // Helper function to create a lighter version of the primary color for hover states
  const getLighterColor = (color: string, opacity: number = 0.1) => {
    return `${color}${Math.round(opacity * 255).toString(16).padStart(2, '0')}`
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3">
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={() => onMonthChange('prev')}
          disabled={!canNavigatePrev() || isLoading}
          className="p-1 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          style={{
            '--hover-bg': getLighterColor(primaryColor, 0.1)
          } as React.CSSProperties}
          onMouseEnter={(e) => {
            if (!e.currentTarget.disabled) {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = getLighterColor(primaryColor, 0.1)
            }
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.backgroundColor = ''
          }}
        >
          <ChevronLeftIcon className="w-4 h-4 text-gray-600" />
        </button>
        
        <h3 className="text-base font-semibold text-gray-900">
          {formatMonthYear(currentMonth)}
        </h3>
        
        <button
          onClick={() => onMonthChange('next')}
          disabled={isLoading}
          className="p-1 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          onMouseEnter={(e) => {
            if (!e.currentTarget.disabled) {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = getLighterColor(primaryColor, 0.1)
            }
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.backgroundColor = ''
          }}
        >
          <ChevronRightIcon className="w-4 h-4 text-gray-600" />
        </button>
      </div>

      {/* Day Names Header */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {dayNames.map((day) => (
          <div key={day} className="p-1 text-center text-xs font-medium text-gray-500">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1">
        {calendarDays.map((date, index) => {
          if (!date) {
            return <div key={index} className="p-1 h-8" />
          }

          const isPast = isDateInPast(date)
          const isAvailable = !isPast && isDateAvailable(date)
          const isSelected = isDateSelected(date)
          const isToday = date.toDateString() === today.toDateString()

          return (
            <button
              key={index}
              onClick={() => isAvailable && onDateSelect(date)}
              disabled={isPast || !isAvailable || isLoading}
              className={`
                relative p-1 h-8 text-xs font-medium rounded-md transition-all
                ${isSelected 
                  ? 'text-white cursor-pointer' 
                  : isAvailable
                  ? 'text-gray-900 cursor-pointer'
                  : isPast
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-400 cursor-not-allowed'
                }
                ${isToday && !isSelected ? 'ring-1 ring-opacity-50' : ''}
              `}
              style={isSelected ? {
                backgroundColor: primaryColor
              } : isToday && !isSelected ? {
                borderColor: primaryColor,
                borderWidth: '1px',
                borderStyle: 'solid'
              } : {}}
              onMouseEnter={(e) => {
                if (isAvailable && !isSelected && !isLoading) {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = getLighterColor(primaryColor, 0.1)
                }
              }}
              onMouseLeave={(e) => {
                if (isAvailable && !isSelected) {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = ''
                }
              }}
            >
              {date.getDate()}
              {isAvailable && !isSelected && (
                <div 
                  className="absolute bottom-0.5 left-1/2 transform -translate-x-1/2 w-1 h-1 rounded-full" 
                  style={{ backgroundColor: primaryColor }}
                />
              )}
              {isLoading && (
                <div className="absolute inset-0 bg-white bg-opacity-50 rounded-md flex items-center justify-center">
                  <div 
                    className="w-3 h-3 border-2 border-t-transparent rounded-full animate-spin" 
                    style={{ borderColor: primaryColor, borderTopColor: 'transparent' }}
                  />
                </div>
              )}
            </button>
          )
        })}
      </div>

      {isLoading && (
        <div className="mt-4 text-center text-sm text-gray-500">
          {t('calendar.checkingAvailability', 'Checking availability...')}
        </div>
      )}
    </div>
  )
}

export default CalendlyStyleCalendar
